'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { getTenancyLength, type Building } from '@repo/api';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import { useAnalytics } from '@/hooks/useAnalytics';
import { RentIncreaseSection } from './RentIncreaseSection';
import { RentIncreaseSectionSkeleton } from './RentIncreaseSectionSkeleton';

interface RentIncreaseProps {
  building: Building;
  buildingAddress: string;
  showSkeleton: boolean;
  isReportReady: boolean;
}

function RentIncrease({
  building,
  buildingAddress,
  showSkeleton,
  isReportReady,
}: RentIncreaseProps) {
  const analytics = useAnalytics();
  const sectionRef = useRef<HTMLDivElement>(null);
  const onScreen = useIntersectionObserver<HTMLDivElement>(
    sectionRef,
    useMemo(() => ({ threshold: 0.5 }), []),
  );

  const [loading, setLoading] = useState(true);
  const [tenancyData, setTenancyData] = useState<any | null>(null);

  useEffect(() => {
    let timer: number | undefined;
    if (onScreen) {
      timer = window.setTimeout(() => {
        analytics.track('rentalreport_rentvalue_scroll');
      }, 5000);
    }
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [analytics, onScreen]);

  useEffect(() => {
    if (!isReportReady) {
      setLoading(false);
      setTenancyData(null);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const response = await getTenancyLength({ buildingId: building.id });
        setTenancyData(response.tenancy);
      } catch (err) {
        console.error('[report][rent-increase] failed to load', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [isReportReady]);

  const shouldShowSkeleton = showSkeleton || loading;
  if (shouldShowSkeleton) return <RentIncreaseSectionSkeleton />;

  return (
    <div ref={sectionRef}>
      <RentIncreaseSection
        buildingAddress={buildingAddress}
        neighborhoodName={building.neighborhood?.name}
        isBlurRequired={!isReportReady}
        tenancyData={tenancyData}
      />
    </div>
  );
}

export default RentIncrease;
